import { Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import LoadingApp from "../../../components/LoadingApp";
import ModalAction from "../../../components/ModalAction";
import ModalFeedBack from "../../../components/ModalFeedBack";
import ListaPermissao from "../../../interfaces/entities/Permissoes/ListaPermissao";
import { PrestadorConsultado } from "../../../interfaces/entities/Permissoes/PrestadorConsultado";
import TipoPermissao from "../../../interfaces/entities/Permissoes/TipoPermissao";
import { PermissionTypeEnum } from "../../../interfaces/enum/PermissionTypeEnum";
import {
  ativarPrestadorConfianca,
  ativarPrestadorConsultado,
  atualizarPermissao,
  desativarPrestadorConfianca,
  desativarPrestadorConsultado,
  getPermissoes,
  getPrestadoresConfianca,
  getPrestadoresConsultados,
} from "../../../services/PermissoesService";
import AllowedDoctorsForm from "./AllowedDoctorsForm";
import NotificationsPermissionForm from "./NotificationsPermissionForm";
import ViewPermissionForm from "./ViewPermissionForm";
import "./styles.css";
import {
  sxDarkGreyBody,
  sxTab,
  sxTabs,
  sxTabsAccessibility,
  sxTitle,
} from "./sxProps";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import PageTitle from "../../../components/Accessibility/PageTitle";

const PermissoesVisualizacao: React.FC = () => {
  const { textStyleAccessibility, textTitleStyleAccessibility, contrastMode } =
    useAccessibility();

  const [currentTab, setCurrentTab] = useState<
    "permissoes" | "medicos-confianca" | "medicos-consultados"
  >("permissoes");
  const [loading, setLoading] = useState<boolean>(false);
  const [examsViewPermission, setExamsViewPermission] =
    useState<TipoPermissao>();
  const [appointmentsViewPermission, setAppointmentsViewPermission] =
    useState<TipoPermissao>();
  const [smsAndNotificationsPermission, setSmsAndNotificationsPermission] =
    useState<TipoPermissao>();
  const [consultedDoctors, setDoctorsConsulted] =
    useState<PrestadorConsultado[]>();
  const [trustedDoctors, setTrustedDoctors] = useState<PrestadorConsultado[]>();

  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: "error" | "success" | "warning";
    title?: string;
    textButton?: string;
    subTitle?: string;
    closeAction: () => void;
    showButtonModal: boolean;
    buttonAction?: () => void;
  }>(null);

  const [modalAction, setModalAction] = useState<null | {
    icon?: "warning";
    title?: string;
    subTitle?: string;
    textContainedButton: string;
    textOutlinedButton: string;
    handleContainedButton: () => void;
    handleOutlinedButton: () => void;
  }>(null);

  useEffect(() => {
    if (
      currentTab === "permissoes" &&
      !examsViewPermission &&
      !appointmentsViewPermission &&
      !smsAndNotificationsPermission
    ) {
      fetchPermissions();
    } else if (currentTab === "medicos-consultados" && !consultedDoctors) {
      fetchDoctorsConsulted();
    } else if (currentTab === "medicos-confianca" && !trustedDoctors) {
      fetchTrustedDoctors();
    }
  }, [currentTab]);

  const fetchDoctorsConsulted = async () => {
    setLoading(true);
    const response = await getPrestadoresConsultados();
    if (response?.status === 200) {
      const consultedDoctors: PrestadorConsultado[] = response.data;
      setDoctorsConsulted(consultedDoctors);
    } else {
      console.error(response);
      openModalError(
        "Erro ao buscar prestadores",
        "Ocorreu um erro ao buscar os médicos consultados. Tente novamente em instantes."
      );
    }
    setLoading(false);
  };

  const fetchTrustedDoctors = async () => {
    setLoading(true);
    const response = await getPrestadoresConfianca();
    if (response?.status === 200) {
      const trustedDoctors: PrestadorConsultado[] = response.data;
      setTrustedDoctors(trustedDoctors);
    } else {
      console.error(response);
      openModalError(
        "Erro ao buscar prestadores",
        "Ocorreu um erro ao buscar os médicos de confiança. Tente novamente em instantes."
      );
    }
    setLoading(false);
  };

  const fetchPermissions = async () => {
    setLoading(true);
    const response = await getPermissoes();
    if (response?.status === 200) {
      const permissionsData: TipoPermissao[] = response.data;
      setPermissions(
        permissionsData,
        PermissionTypeEnum.VisualizacaoExames,
        setExamsViewPermission
      );
      setPermissions(
        permissionsData,
        PermissionTypeEnum.VisualizacaoConsultas,
        setAppointmentsViewPermission
      );
      setPermissions(
        permissionsData,
        PermissionTypeEnum.SmsNotificacoes,
        setSmsAndNotificationsPermission
      );
    } else {
      console.error(response);
      openModalError(
        "Erro ao consultar permissões",
        "Ocorreu um erro ao consultar as permissões. Tente novamente em instantes."
      );
    }
    setLoading(false);
  };

  const toggleConsultedDoctorPermission = async (
    doctor: PrestadorConsultado
  ) => {
    setLoading(true);
    let doctorsResponse;
    switch (doctor.indAtivo) {
      case true:
        doctorsResponse = await desativarPrestadorConsultado(
          doctor.idPrestador,
          doctor.idPermisBenefPrestador
        );
        break;
      case false:
        doctorsResponse = await ativarPrestadorConsultado(
          doctor.idPrestador,
          doctor.idPermisBenefPrestador
        );
    }
    if (doctorsResponse?.status === 200) {
      setDoctorsConsulted(doctorsResponse?.data);
    } else {
      openModalError(
        "Erro ao atualizar permissão",
        "Ocorreu um erro ao atualizar a permissão. Tente novamente em instantes."
      );
    }
    setLoading(false);
  };

  const confirmPermissionChange = async (newPermission: ListaPermissao) => {
    setModalAction(null);
    return await atualizarPermissoes(newPermission);
  };

  const cancelPermissionChange = () => {
    setModalAction(null);
  };

  const toggleTrustedDoctorPermission = async (doctor: PrestadorConsultado) => {
    setLoading(true);
    let doctorsResponse;
    switch (doctor.indAtivo) {
      case true:
        doctorsResponse = await desativarPrestadorConfianca(
          doctor.idPrestador,
          doctor.idPermisBenefPrestador
        );
        break;
      case false:
        doctorsResponse = await ativarPrestadorConfianca(
          doctor.idPrestador,
          doctor.idPermisBenefPrestador
        );
    }
    if (doctorsResponse?.status === 200) {
      setTrustedDoctors(doctorsResponse?.data);
    } else {
      openModalError(
        "Erro ao atualizar permissão",
        "Ocorreu um erro ao atualizar a permissão. Tente novamente em instantes."
      );
    }
    setLoading(false);
  };

  const togglePermission = async (newPermission: ListaPermissao) => {
    if (newPermission.idPermissao === "10") {
      //ID PERMISSÃO PARA 'BLOQUEAR TOTALMENTE'
      openModalConfirmTotallyBlock(
        "Atenção",
        `Ao selecionar esta opção, você fica ciente de que as suas solicitações,
      realizações e resultados de exames não estarão visíveis na Rede de Atendimento da Unimed Campinas,
      sendo necessário que, no momento da realização dos procedimentos e exames, você tenha em mãos o número da
      Guia de Autorização da Unimed Campinas, bem como os resultados dos exames realizados para mostrar ao médico quando necessário.`,
        () => confirmPermissionChange(newPermission),
        cancelPermissionChange
      );
    } else {
      await atualizarPermissoes(newPermission);
    }
  };

  const atualizarPermissoes = async (newPermission: ListaPermissao) => {
    setLoading(true);
    const newPermissionsResponse = await atualizarPermissao(newPermission);
    if (newPermissionsResponse?.status === 200) {
      const updatedPermissions = newPermissionsResponse.data;
      setPermissions(
        updatedPermissions,
        PermissionTypeEnum.VisualizacaoExames,
        setExamsViewPermission
      );
      setPermissions(
        updatedPermissions,
        PermissionTypeEnum.VisualizacaoConsultas,
        setAppointmentsViewPermission
      );
      setPermissions(
        updatedPermissions,
        PermissionTypeEnum.SmsNotificacoes,
        setSmsAndNotificationsPermission
      );
    } else {
      console.error(newPermissionsResponse);
    }
    setLoading(false);
  };

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: "error",
      title: title,
      subTitle: subtitle,
      textButton: "Fechar",
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null),
    });
  };

  const openModalConfirmTotallyBlock = (
    title: string,
    subtitle: string,
    onConfirm: () => void,
    onCancel: () => void
  ) => {
    setModalAction({
      icon: "warning",
      title: title,
      subTitle: subtitle,
      textContainedButton: "BLOQUEAR ACESSOS",
      handleContainedButton: onConfirm,
      textOutlinedButton: "MANTER OPÇÃO ATUAL",
      handleOutlinedButton: onCancel,
    });
  };

  const setPermissions = (
    permissionsData: TipoPermissao[],
    idTipoPermissao: string,
    setter: React.Dispatch<React.SetStateAction<TipoPermissao | undefined>>
  ) => {
    const permission = permissionsData.find(
      (permission) => permission.idTipoPermissao === idTipoPermissao
    );
    setter(permission);
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(
      newValue as "permissoes" | "medicos-consultados" | "medicos-confianca"
    );
  };

  return (
    <>
      <LoadingApp showLoading={loading} />
      <div className="permissions">
        <PageTitle text="Minhas permissões" />
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          sx={contrastMode ? sxTabsAccessibility : sxTabs}
          textColor="inherit"
        >
          <Tab
            tabIndex={0}
            value="permissoes"
            label="Permissões"
            style={textStyleAccessibility}
            sx={sxTab}
            disableRipple
            disableTouchRipple
            disableFocusRipple
          />
          <Tab
            tabIndex={0}
            value="medicos-confianca"
            label="Médicos de confiança"
            style={textStyleAccessibility}
            sx={sxTab}
            disableRipple
            disableTouchRipple
            disableFocusRipple
          />
          <Tab
            tabIndex={0}
            value="medicos-consultados"
            label="Médicos consultados"
            style={textStyleAccessibility}
            sx={sxTab}
            disableRipple
            disableTouchRipple
            disableFocusRipple
          />
        </Tabs>
        {currentTab === "permissoes" && (
          <>
            <div>
              <Typography style={textStyleAccessibility} sx={sxDarkGreyBody}>
                Nesta seção, você pode selecionar qual opção deseja autorizar:
                <br />
                <br />
                &nbsp;&bull;&nbsp;&nbsp;Quem poderá visualizar suas últimas
                consultas realizadas;
                <br />
                &nbsp;&bull;&nbsp;&nbsp;Se você aceita receber mensagens de SMS
                sobre determinados assuntos;
                <br />
                &nbsp;&bull;&nbsp;&nbsp;Quem poderá visualizar seus exames
                solicitados por algum médico da Unimed.
              </Typography>
            </div>

            <>
              <ViewPermissionForm
                formData={examsViewPermission}
                formTitle="Visualização de exames"
                toggleOption={(newOption) => togglePermission(newOption)}
              />
              <ViewPermissionForm
                formData={appointmentsViewPermission}
                formTitle="Visualização das últimas consultas"
                toggleOption={(newOption) => togglePermission(newOption)}
              />
              <NotificationsPermissionForm
                formData={smsAndNotificationsPermission}
                formTitle="Recebimento de SMS/notificações"
                onPermissionChange={(newOption) => togglePermission(newOption)}
              />
            </>
          </>
        )}
        {currentTab === "medicos-consultados" && (
          <AllowedDoctorsForm
            doctors={consultedDoctors}
            toggleDoctorPermission={toggleConsultedDoctorPermission}
            doctorsType="consulted"
          />
        )}
        {currentTab === "medicos-confianca" && (
          <AllowedDoctorsForm
            doctors={trustedDoctors}
            toggleDoctorPermission={toggleTrustedDoctorPermission}
            doctorsType="trusted"
            fetchDoctors={fetchTrustedDoctors}
          />
        )}
      </div>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as "success" | "error" | "warning"}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
      <ModalAction
        showModal={!!modalAction}
        handleShowModal={modalAction?.handleOutlinedButton}
        title={modalAction?.title as string}
        subTitle={modalAction?.subTitle as string}
        icon={modalAction?.icon as "success" | "error" | "warning"}
        textContainedButton={modalAction?.textContainedButton || ""}
        handleContainedButton={modalAction?.handleContainedButton}
        textOutlinedButton={modalAction?.textOutlinedButton || ""}
        handleOutlinedButton={modalAction?.handleOutlinedButton}
      />
    </>
  );
};

export default PermissoesVisualizacao;
